<template>
    <DeprecatedKForm :submit="submit" class="patient-registration" columns="3">
        <template #header>
            <h2 v-if="!editPatient">Skapa patient</h2>
            <h2 v-else>Redigera patient</h2>
        </template>
        <template #form-content>
            <CollapsibleSection heading="Folkbokföringsuppgifter" number="1">
                <p v-if="editPatient && formData.updated_at" class="last-updated">
                    {{ `Senast uppdaterat ${dateFormatter.format(new Date(formData.updated_at))}` }}
                </p>
                <div class="form-container">
                    <div class="form-grouping three">
                        <input-wrapper
                            :component="widgets.TextWidget"
                            :label="externalPatientDataLabels.personId"
                            name="personId"
                            v-model="formData.personId"
                            :disabled="editPatient"
                            :required="true"
                            @input="getDateOfBirthIfPersonnummer"
                        />
                        <input-wrapper
                            :component="widgets.SelectWidget"
                            label="Typ av personID"
                            name="personIdType"
                            @input="personIdTypeChanged"
                            v-model="formData.personIdType"
                            apisearch="/core/kodverk/personidtypes/"
                            :map="
                                (el) => {
                                    return { value: el.code, text: el.displayName };
                                }
                            "
                            :required="true"
                            :no-results="true"
                            :defaultValue="'Personnummer'"
                        />
                        <Button
                            v-if="purConfigured"
                            size="secondary"
                            type="button"
                            class="button"
                            @click="fetchPersonuppgifter"
                            :disabled="!personIdTypeIsPersonnummer"
                        >
                            <img src="@/assets/download_white.svg" />
                            {{ editPatient ? "Uppdatera personuppgifter" : "Hämta personuppgifter" }}
                        </Button>
                    </div>
                    <div class="form-grouping three">
                        <input-wrapper
                            :component="widgets.SelectWidget"
                            :options="tilltalsnamnOptions"
                            label="Tilltalsnamn"
                            name="tilltalsnamn"
                            v-model="formData.tilltalsnamn_index"
                        />
                        <input-wrapper
                            :component="widgets.TextWidget"
                            :label="externalPatientDataLabels.fornamn"
                            name="fornamn"
                            @input="fornamnChanged"
                            v-model="formData.fornamn"
                            :required="true"
                            :maxlength="100"
                        />
                        <input-wrapper
                            :component="widgets.TextWidget"
                            :label="externalPatientDataLabels.efternamn"
                            name="efternamn"
                            v-model="formData.efternamn"
                            :required="true"
                            :maxlength="100"
                        />
                    </div>
                    <div class="form-grouping two">
                        <input-wrapper
                            :component="widgets.DateWidget"
                            :label="externalPatientDataLabels.dateOfBirth"
                            name="dateOfBirth"
                            v-model="formData.dateOfBirth"
                            :required="true"
                        />
                        <input-wrapper
                            :component="widgets.SelectWidget"
                            :label="externalPatientDataLabels.gender"
                            name="gender"
                            v-model="formData.gender"
                            apisearch="/core/kodverk/genders/"
                            :map="
                                (el) => {
                                    return { value: el.code, text: el.displayName };
                                }
                            "
                            :required="true"
                            :no-results="true"
                        />
                    </div>
                    <div class="form-grouping three">
                        <input-wrapper
                            :component="widgets.TextWidget"
                            :label="externalPatientDataLabels.adressRad1"
                            name="adressRad1"
                            v-model="formData.adressRad1"
                        />
                        <input-wrapper
                            :component="widgets.TextWidget"
                            :label="externalPatientDataLabels.adressRad2"
                            name="adressRad2"
                            v-model="formData.adressRad2"
                        />
                        <input-wrapper
                            :component="widgets.TextWidget"
                            :label="externalPatientDataLabels.coAdress"
                            name="coAdress"
                            v-model="formData.coAdress"
                        />
                    </div>
                    <div class="form-grouping three">
                        <input-wrapper
                            :component="widgets.TextWidget"
                            :label="externalPatientDataLabels.postnummer"
                            name="postnummer"
                            v-model="formData.postnummer"
                            :maxlength="5"
                        />
                        <input-wrapper
                            :component="widgets.TextWidget"
                            :label="externalPatientDataLabels.postort"
                            name="postort"
                            v-model="formData.postort"
                        />
                        <input-wrapper
                            :component="widgets.SelectWidget"
                            :options="lan"
                            :label="externalPatientDataLabels.lan"
                            name="lan"
                            v-model="formData.lan"
                        />
                    </div>
                    <div class="form-grouping">
                        <input-wrapper
                            readonly
                            disabled
                            :component="widgets.CheckboxWidget"
                            :checkbox-label="externalPatientDataLabels.sekretess"
                            name="sekretess"
                            v-model="formData.sekretess"
                        />
                        <input-wrapper
                            readonly
                            disabled
                            :component="widgets.CheckboxWidget"
                            :checkbox-label="externalPatientDataLabels.skyddad_identitet"
                            name="skyddad_identitet"
                            v-model="formData.skyddad_identitet"
                        />
                        <input-wrapper
                            readonly
                            disabled
                            :component="widgets.CheckboxWidget"
                            :checkbox-label="externalPatientDataLabels.avliden"
                            name="avliden"
                            v-model="formData.avliden"
                        />
                        <input-wrapper
                            :component="widgets.CheckboxWidget"
                            :checkbox-label="externalPatientDataLabels.careCoverage"
                            :disabled="personIdTypeIsPersonnummer"
                            name="careCoverage"
                            v-model="formData.careCoverage"
                        />
                    </div>
                </div>
            </CollapsibleSection>
            <CollapsibleSection heading="Telefon och e-post" number="2">
                <div class="form-container">
                    <input-wrapper
                        :component="widgets.TextWidget"
                        label="Telefon"
                        name="telefon"
                        v-model="formData.telefon"
                    />
                    <input-wrapper
                        :component="widgets.CheckboxWidget"
                        value="false"
                        checkbox-label="Önskar sms"
                        name="onskar_sms"
                        v-model="formData.onskar_sms"
                    />
                    <input-wrapper
                        :component="widgets.TextWidget"
                        label="E-post"
                        name="email"
                        v-model="formData.email"
                    />
                </div>
            </CollapsibleSection>
        </template>
        <template #footer>
            <ButtonGroup class="form-group">
                <Button type="submit" size="primary" :disabled="!hasUnsavedData">
                    {{ editPatient ? "Uppdatera patient" : "Skapa patient" }}
                </Button>
                <Button type="button" size="secondary" variant="contour" @click="close()">Avbryt</Button>
            </ButtonGroup>
        </template>
    </DeprecatedKForm>
</template>
<script>
    import widgets from "@/components/widgets/inputs";
    import InputWrapper from "@/components/widgets/inputs/InputWrapper.vue";
    import BaseTab from "@/tabs/mixins/BaseTab";
    import { flashMessage, flashWarning, openDialog } from "@/utils";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import { computed } from "vue";
    import { mapGetters } from "vuex";
    import tabEvents from "@/tabEvents";
    import tabIcon from "@/assets/receptforskrivning.svg";
    import Button from "@/components/Button.vue";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import { cloneDeep, isEqual } from "lodash";
    import CollapsibleSection from "@/components/generic/CollapsibleSection.vue";
    import DeprecatedKForm from "@/components/generic/form/DeprecatedKForm.vue";
    import { externalPatientDataLabels, updatePurExternalPatientData } from "@/utils/patient/externalPatientData";
    import { useStore } from "@/store";
    import { PersonIdTyp } from "@/utils/constants";

    export default {
        extends: BaseTab,
        name: "PatientRegistration",
        tabName: "Skapa patient",
        icon: tabIcon,
        patientBound: false,

        props: {
            tabId: Number,
            parameters: {
                type: Object,
                required: false,
            },
        },

        setup() {
            const store = useStore();

            const purConfigured = computed(() => store.getters["systemData/purConfigured"]);

            return {
                purConfigured,
            };
        },

        components: {
            DeprecatedKForm,
            CollapsibleSection,
            ...widgets,
            InputWrapper,
            Button,
            ButtonGroup,
        },
        data() {
            return {
                defaultFormData: {},
                formData: {},
                widgets: widgets,
                lan: [],
                dateFormatter: new Intl.DateTimeFormat("se"),
                tilltalsnamnOptions: [],
            };
        },
        watch: {
            /* Det här känns funky, behöver uppdatera tilltalsnamn-selecten baserat på värdet i förnamn */
            formData(newValue) {
                if (newValue.fornamn) {
                    this.tilltalsnamnOptions = newValue.fornamn.split(" ").map((strVal, index) => {
                        return {
                            value: index,
                            text: strVal,
                        };
                    });

                    // Lägg till ett tomt alternativ för att nollställa
                    this.tilltalsnamnOptions.unshift({ value: -1, text: "-" });
                }
            },
        },
        computed: {
            externalPatientDataLabels() {
                return externalPatientDataLabels;
            },
            ...mapGetters("patientData", ["editPatient", "currentId"]),
            hasUnsavedData() {
                return !isEqual(this.defaultFormData, this.formData);
            },
            personIdTypeIsPersonnummer() {
                return this.formData.personIdType === PersonIdTyp.PERSONNUMMER;
            },
        },
        methods: {
            async submit() {
                let details = this.formData;
                const config = getConfig();

                details.sekretess = Boolean(details.sekretess);
                details.skyddad_identitet = Boolean(details.skyddad_identitet);
                details.onskar_sms = Boolean(details.onskar_sms);
                details.avliden = Boolean(details.avliden);

                try {
                    const response = await (this.editPatient
                        ? klinikenApi.put("/core/patienter/" + this.editPatient + "/", details, config)
                        : klinikenApi.post("/core/patienter/", details, config));

                    if (this.currentId && this.editPatient === this.currentId) {
                        this.$store.commit("patientData/load", response.data);
                    }

                    await this.$store.dispatch("patientData/loadFromBackend", {
                        id: response.data.id,
                        failSilently: false,
                    });

                    flashMessage("Patient sparad.");
                    await this.$store.dispatch("tabs/closeTab", this.tabId);
                    tabEvents.$emit("refreshPatientSearch", {});
                } catch (error) {
                    openDialog(`Kunde inte spara patient. ${getErrorMessage(error, false)}`, "error");
                }
            },
            close() {
                this.closeTab(this.hasUnsavedData);
            },
            async getPatient(id) {
                let patient = null;
                const config = getConfig();
                await klinikenApi
                    .get(`core/patienter/${id}/`, config)
                    .then((response) => {
                        patient = response.data;
                    })
                    .catch((e) => {
                        openDialog("Kunde inte hämta patient från servern." + getErrorMessage(e), "warning");
                    });
                return patient;
            },
            setDateOfBirth({ dateString }) {
                if (!this.formData.dateOfBirth && dateString && dateString.length === 12) {
                    this.formData.dateOfBirth = `${this.formData.personId.slice(0, 4)}-${this.formData.personId.slice(
                        4,
                        6
                    )}-${this.formData.personId.slice(6, 8)}`;
                }
            },
            getDateOfBirthIfPersonnummer() {
                if (this.formData.personIdType === PersonIdTyp.PERSONNUMMER) {
                    this.setDateOfBirth({ dateString: this.formData.personId });
                }
            },
            personIdTypeChanged(eventValue) {
                if (eventValue === PersonIdTyp.PERSONNUMMER) {
                    this.setDateOfBirth({ dateString: this.formData.personId });
                    this.formData.careCoverage = true;
                } else {
                    this.formData.dateOfBirth = null;
                }
            },
            fornamnChanged(eventValue) {
                this.tilltalsnamnOptions = this.formData.fornamn.split(" ").map((strVal, index) => {
                    return { value: index + 1, text: strVal };
                });

                // Lägg till ett tomt alternativ för att nollställa
                this.tilltalsnamnOptions.unshift({ value: -1, text: "-" });
            },
            async fetchPersonuppgifter() {
                const newPatientData = await updatePurExternalPatientData({
                    personId: this.formData.personId,
                    action: this.editPatient ? "edit" : "create",
                    currentPatientData: this.formData,
                    tabId: this.tabId,
                });

                if (newPatientData) {
                    this.formData = { ...newPatientData };

                    if (this.editPatient) {
                        this.defaultFormData = { ...newPatientData };
                    }
                }
            },
            async getLanOptions() {
                try {
                    const result = await klinikenApi.get("core/kodverk/lanskoder", getConfig({}));

                    this.lan = result.data.map(([value, label]) => ({ value, text: label }));
                } catch (error) {
                    flashWarning("Kunde inte hämta tillgängliga län");
                }
            },
        },
        async created() {
            await this.getLanOptions();

            if (this.editPatient) {
                let patient = await this.getPatient(this.editPatient);

                this.defaultFormData = cloneDeep(patient);
                this.formData = patient;

                this.tilltalsnamnOptions = this.formData.fornamn.split(" ").map((strVal, index) => {
                    return { value: index + 1, text: strVal };
                });

                // Lägg till ett tomt alternativ för att nollställa
                this.tilltalsnamnOptions.unshift({ value: -1, text: "-" });

                if (patient.personIdType === PersonIdTyp.PERSONNUMMER) {
                    this.formData.careCoverage = true;
                }

                this.$store.commit("tabs/setPatientBound", {
                    id: this.tabId,
                    flag: true,
                });
                this.$store.commit("tabs/setTabName", { id: this.tabId, name: "Redigera patient" });
            }
        },
        beforeDestroy() {
            this.$store.commit("patientData/setEditPatient", null);
        },
    };
</script>
<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/container";

    .patient-registration {
        container-type: inline-size;
        width: $content-max-width;
        max-width: 100%;

        > h2:first-child {
            margin-bottom: 1rem;
        }

        .last-updated {
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
        }
    }
</style>
